import { useMemo, type ReactNode } from 'react';
import { I18nContext, type I18nContextValue, type TranslationMap } from './context';

type InterpolationValue = string | number;

function resolvePath(source: TranslationMap, key: string): unknown {
  return key.split('.').reduce<unknown>((current, segment) => {
    if (current && typeof current === 'object' && segment in (current as TranslationMap)) {
      return (current as TranslationMap)[segment];
    }
    return undefined;
  }, source);
}

function interpolate(template: string, params?: Record<string, InterpolationValue>): string {
  if (!params) {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = params[key];
    return value === undefined ? `{${key}}` : String(value);
  });
}

interface I18nProviderProps {
  locale: string;
  messages: TranslationMap;
  fallbackMessages?: TranslationMap;
  children: ReactNode;
}

export function I18nProvider({
  locale,
  messages,
  fallbackMessages = {},
  children,
}: I18nProviderProps) {
  const value = useMemo<I18nContextValue>(() => {
    const get = <T,>(key: string, fallback?: T): T => {
      const primary = resolvePath(messages, key);
      if (primary !== undefined) {
        return primary as T;
      }

      const fallbackValue = resolvePath(fallbackMessages, key);
      if (fallbackValue !== undefined) {
        return fallbackValue as T;
      }

      return fallback as T;
    };

    const t = (key: string, params?: Record<string, InterpolationValue>): string => {
      const resolved = get<string | undefined>(key);
      if (typeof resolved === 'string') {
        return interpolate(resolved, params);
      }
      return key;
    };

    return { locale, t, get };
  }, [locale, messages, fallbackMessages]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
